import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ZoomableImageModal } from '@/components/ZoomableImageModal';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import type { AgreementStatus } from '@/lib/database.types';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/lib/functions';
import { fetchRequest, cancelServiceRequest, type ServiceRequest } from '@/lib/serviceRequests';
import { listOffersForRequest, type Offer } from '@/lib/offers';
import { supabase } from '@/lib/supabase';

const OFFER_STATUS_COLOR: Record<string, string> = {
  sent: '#F59E0B',
  accepted: '#22C55E',
  declined: '#EF4444',
  expired: '#6B7280',
};

export default function RequestDetailScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [requesterName, setRequesterName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [imageViewerUri, setImageViewerUri] = useState<string | null>(null);

  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offerCreatorNames, setOfferCreatorNames] = useState<Record<string, string>>({});
  const [agreementStatuses, setAgreementStatuses] = useState<Record<string, AgreementStatus>>({});

  const isOwner = !!request && !!user && request.user_id === user.id;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await fetchRequest(id);
      setRequest(r);
      if (r) {
        const { data } = await supabase.rpc('get_profiles_public', { p_ids: [r.user_id] });
        setRequesterName((data?.[0]?.full_name as string | null)?.trim() || null);
        if (user && r.user_id === user.id) {
          setOffersLoading(true);
          try {
            const list = await listOffersForRequest(r.id);
            setOffers(list);
            const creatorIds = [...new Set(list.map((o) => o.creator_id))];
            if (creatorIds.length > 0) {
              const { data: profiles } = await supabase.rpc('get_profiles_public', { p_ids: creatorIds });
              const personalNames: Record<string, string> = {};
              const businessNamesById: Record<string, string> = {};
              for (const p of (profiles ?? []) as { id: string; full_name: string | null; business_name: string | null; show_business_name: boolean | null; business_id: string | null }[]) {
                const personal = p.full_name?.trim() || '—';
                personalNames[p.id] = (p.show_business_name && p.business_name?.trim())
                  ? (p.business_id ? `${p.business_name.trim()} — ${personal}` : p.business_name.trim())
                  : personal;
              }
              // Αν ένα αίτημα στάλθηκε "ως η επιχείρηση" (sender_business_id), το
              // όνομα της επιχείρησης υπερισχύει — κλειδωμένο ανά offer.id, όχι
              // ανά creator_id, γιατί ο ίδιος μπορεί να στέλνει άλλοτε προσωπικά.
              const senderBusinessIds = [...new Set(list.map((o) => (o as unknown as { sender_business_id?: string | null }).sender_business_id).filter((v): v is string => !!v))];
              if (senderBusinessIds.length > 0) {
                const { data: businesses } = await supabase.from('businesses').select('id, name').in('id', senderBusinessIds);
                for (const b of (businesses ?? []) as { id: string; name: string }[]) businessNamesById[b.id] = b.name;
              }
              const names: Record<string, string> = {};
              for (const o of list) {
                const senderBusinessId = (o as unknown as { sender_business_id?: string | null }).sender_business_id;
                names[o.id] = (senderBusinessId && businessNamesById[senderBusinessId])
                  || personalNames[o.creator_id]
                  || '—';
              }
              setOfferCreatorNames(names);
            }
            const agreementIds = [...new Set(list.filter((o) => o.status === 'accepted' && o.agreement_id).map((o) => o.agreement_id!))];
            if (agreementIds.length > 0) {
              const { data: agreements } = await supabase.from('agreements').select('id, status').in('id', agreementIds);
              const statuses: Record<string, AgreementStatus> = {};
              for (const a of (agreements ?? []) as { id: string; status: AgreementStatus }[]) statuses[a.id] = a.status;
              setAgreementStatuses(statuses);
            }
          } catch {
            setOffers([]);
          } finally {
            setOffersLoading(false);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  async function onCancel() {
    if (!request) return;
    appAlert(t('requests.cancelTitle'), t('requests.cancelBody'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('requests.action_cancelRequest'),
        style: 'destructive',
        onPress: async () => {
          setBusy(true);
          try {
            await cancelServiceRequest(request.id);
            await load();
          } catch (e) {
            appAlert(t('common.error'), getErrorMessage(e, t('requests.errorCreateFailed')));
          } finally {
            setBusy(false);
          }
        },
      },
    ]);
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!request) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>{t('requests.notFound')}</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={[styles.content, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl }]}>
      <Pressable onPress={() => router.back()} style={styles.back}>
        <Text style={styles.backText}>{t('common.back')}</Text>
      </Pressable>

      <Text style={styles.title}>{request.title}</Text>
      {request.status !== 'open' && (
        <View style={styles.statusBadge}>
          <Text style={styles.statusBadgeText}>
            {request.status === 'closed' ? t('requests.status_closed') : t('requests.status_cancelled')}
          </Text>
        </View>
      )}
      {!!request.description && <Text style={styles.desc}>{request.description}</Text>}
      {!!request.image_url && (
        <Pressable onPress={() => setImageViewerUri(request.image_url)} accessibilityRole="button">
          <Image source={{ uri: request.image_url }} style={styles.requestImage} resizeMode="cover" />
        </Pressable>
      )}

      {isOwner ? (
        <>
          {request.status === 'open' && (
            <View style={{ flexDirection: 'row', gap: spacing.sm }}>
              <View style={{ flex: 1 }}>
                <Button
                  title={t('requests.action_edit')}
                  variant="secondary"
                  onPress={() => router.push({ pathname: '/(app)/requests/new', params: { editId: request.id } } as any)}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Button title={t('requests.action_cancelRequest')} variant="secondary" danger loading={busy} onPress={onCancel} />
              </View>
            </View>
          )}

          <Text style={styles.offersSectionTitle}>
            {t('requests.offersReceived', { count: offers.length })}
          </Text>

          {offersLoading ? (
            <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.md }} />
          ) : offers.length === 0 ? (
            <Text style={styles.emptyText}>{t('requests.noOffersYet')}</Text>
          ) : (
            <View style={{ gap: spacing.sm }}>
              {offers.map((o) => (
                <Pressable
                  key={o.id}
                  style={styles.offerCard}
                  onPress={() => router.push({ pathname: '/(app)/offers/[id]', params: { id: o.id } })}
                  accessibilityRole="button"
                >
                  <View style={styles.offerCardTop}>
                    <Text style={styles.offerCardName} numberOfLines={1}>{offerCreatorNames[o.id] ?? '—'}</Text>
                    {o.status === 'accepted' && o.agreement_id && agreementStatuses[o.agreement_id] ? (
                      <StatusBadge status={agreementStatuses[o.agreement_id]} />
                    ) : (
                      <View style={[styles.offerStatusBadge, { backgroundColor: `${OFFER_STATUS_COLOR[o.status] ?? colors.textMuted}22` }]}>
                        <Text style={[styles.offerStatusBadgeText, { color: OFFER_STATUS_COLOR[o.status] ?? colors.textMuted }]}>
                          {t(`offers.status.${o.status === 'sent' ? 'pending' : o.status}`)}
                        </Text>
                      </View>
                    )}
                  </View>
                  {o.cost != null && (
                    <Text style={styles.offerCardCost}>{o.cost.toFixed(2)} {o.currency}</Text>
                  )}
                  <Ionicons name="chevron-forward" size={16} color={colors.textMuted} style={styles.offerCardChevron} />
                </Pressable>
              ))}
            </View>
          )}
        </>
      ) : (
        request.status === 'open' && (
          <Button
            title={t('requests.action_sendOffer')}
            onPress={() => router.push({
              pathname: '/(app)/offers/new',
              params: { recipientId: request.user_id, recipientName: requesterName ?? t('common.selectedUser'), presetTitle: request.title, serviceRequestId: request.id },
            })}
          />
        )
      )}

      <ZoomableImageModal uri={imageViewerUri} onClose={() => setImageViewerUri(null)} />
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    content: { paddingHorizontal: spacing.lg, gap: spacing.md },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background },
    back: { alignSelf: 'flex-start' },
    backText: { color: colors.primary, fontSize: fontSize.md, fontWeight: '600' },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    desc: { color: colors.textMuted, fontSize: fontSize.md, lineHeight: 22 },
    requestImage: {
      width: '100%',
      height: 220,
      borderRadius: radius.md,
      backgroundColor: colors.surfaceAlt,
    },
    statusBadge: {
      alignSelf: 'flex-start', backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full, paddingHorizontal: 10, paddingVertical: 4,
    },
    statusBadgeText: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '700' },
    emptyText: { color: colors.textMuted, fontSize: fontSize.sm },
    offersSectionTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', marginTop: spacing.sm },
    offerCard: {
      backgroundColor: colors.surface, borderRadius: radius.md, borderWidth: 1,
      borderColor: colors.border, padding: spacing.md, gap: 4, position: 'relative',
    },
    offerCardTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingRight: 20 },
    offerCardName: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    offerStatusBadge: { borderRadius: radius.full, paddingHorizontal: 8, paddingVertical: 3 },
    offerStatusBadgeText: { fontSize: 11, fontWeight: '700' },
    offerCardCost: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    offerCardChevron: { position: 'absolute', top: spacing.md, right: spacing.md },
  });
}
